import { withDeadline } from "@birdwatch/common";
import { useEffect, useState } from "react";
import type { pdfjs } from "react-pdf";

type PdfDocument = Awaited<ReturnType<typeof pdfjs.getDocument>["promise"]>;

/** About twice the widest page card, so the page stays sharp on a retina screen. */
const THUMBNAIL_WIDTH_PX = 320;
/** A long receipt would otherwise paint a canvas thousands of pixels tall; the card shows its middle anyway. */
const MAX_THUMBNAIL_HEIGHT_PX = THUMBNAIL_WIDTH_PX * 2;
const JPEG_QUALITY = 0.82;
const RENDER_TIMEOUT_MS = 20_000;
/** A grid of forty PDFs must not start forty downloads at once. */
const MAX_CONCURRENT_RENDERS = 2;
const MAX_CACHED_PAGES = 150;

export type PdfFirstPage =
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error" };

type CacheEntry = { promise: Promise<string>; settled?: PdfFirstPage };

// A UI-session cache, keyed by the PDF's URL: every tile showing the same file shares
// one render, and a failure is remembered so a corrupt or locked PDF is fetched once.
const cache = new Map<string, CacheEntry>();

let activeRenders = 0;
const waitingRenders: Array<() => void> = [];

/**
 * The first page of the PDF at `src`, as a JPEG object URL — rendered once, then
 * served from the cache. `enabled: false` (an off-screen tile) starts nothing.
 */
export function usePdfFirstPage(src: string, enabled: boolean): PdfFirstPage {
  const [state, setState] = useState(() => ({ src, page: peekFirstPage(src) }));
  // A new src deserves its own answer — never a frame of the previous file's page.
  const page = state.src === src ? state.page : peekFirstPage(src);

  useEffect(() => {
    if (!enabled) return;
    const entry = getFirstPage(src);
    if (entry.settled) {
      setState({ src, page: entry.settled });
      return;
    }
    let cancelled = false;
    entry.promise.then(
      (url) => !cancelled && setState({ src, page: { status: "ready", url } }),
      () => !cancelled && setState({ src, page: { status: "error" } }),
    );
    return () => {
      cancelled = true;
    };
  }, [src, enabled]);

  return page;
}

function peekFirstPage(src: string): PdfFirstPage {
  return cache.get(src)?.settled ?? { status: "loading" };
}

function getFirstPage(src: string): CacheEntry {
  const hit = cache.get(src);
  if (hit) {
    // Re-insert so the Map's order stays least-recently-used first.
    cache.delete(src);
    cache.set(src, hit);
    return hit;
  }

  const entry: CacheEntry = {
    promise: withRenderSlot(() => renderPdfFirstPage(src)),
  };
  entry.promise.then(
    (url) => {
      entry.settled = { status: "ready", url };
    },
    (error: unknown) => {
      entry.settled = { status: "error" };
      console.warn(`PDF thumbnail failed for ${src}`, error);
    },
  );
  cache.set(src, entry);
  evictOldestPages();
  return entry;
}

function evictOldestPages() {
  for (const [src, entry] of cache) {
    if (cache.size <= MAX_CACHED_PAGES) return;
    cache.delete(src);
    entry.promise.then(
      (url) => URL.revokeObjectURL(url),
      () => undefined,
    );
  }
}

async function withRenderSlot<T>(task: () => Promise<T>): Promise<T> {
  if (activeRenders < MAX_CONCURRENT_RENDERS) activeRenders++;
  // A finishing render hands its slot straight to the next waiter, so a newcomer can
  // never slip in between and push the count over the limit.
  else await new Promise<void>((resolve) => waitingRenders.push(resolve));

  try {
    return await task();
  } finally {
    const next = waitingRenders.shift();
    if (next) next();
    else activeRenders--;
  }
}

async function renderPdfFirstPage(src: string): Promise<string> {
  // Loaded on first use: a tile that never shows a PDF never downloads pdf.js.
  const { pdfjs } = await import("@/components/ui/pdf-viewer/pdfjs");
  const loadingTask = pdfjs.getDocument({
    url: src,
    // Only page 1 is read — don't prefetch the rest when the host serves ranges.
    disableAutoFetch: true,
    isEvalSupported: false,
  });

  try {
    return await withDeadline(
      loadingTask.promise.then(drawFirstPage),
      RENDER_TIMEOUT_MS,
      () => new Error(`PDF thumbnail timed out after ${RENDER_TIMEOUT_MS}ms`),
    );
  } finally {
    // Frees the parsed document in the worker; only the small JPEG outlives the render.
    void loadingTask.destroy();
  }
}

async function drawFirstPage(pdf: PdfDocument): Promise<string> {
  const page = await pdf.getPage(1);
  const { width } = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: THUMBNAIL_WIDTH_PX / width });

  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.min(Math.ceil(viewport.height), MAX_THUMBNAIL_HEIGHT_PX);
  await page.render({ canvas, viewport }).promise;

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) throw new Error("PDF thumbnail could not be encoded");
  return URL.createObjectURL(blob);
}
